import { useEffect, useRef, useState, type FormEvent } from "react";
import { canManageCourtBookings } from "../lib/access";
import { ApiError } from "../lib/api/client";
import { createCourtBlock, deleteCourtBlock, listCourtBlocks, listCourts } from "../lib/api/endpoints";
import { BLOCK_REASON_TYPE, blockReasonTypeLabel } from "../lib/enums";
import { useSession } from "../lib/session/SessionContext";
import { C, fieldStyle, font } from "../theme";
import type { CourtBlockDto, CourtBookingDto, CourtDto, CreateCourtBlockRequest } from "../types";
import { Button } from "./Button";
import { Card } from "./Card";
import { ConfirmDialog } from "./ConfirmDialog";
import { DateTimePicker } from "./DateTimePicker";
import { FieldInput, FieldTextarea } from "./Field";
import { Label } from "./Label";

const labelStyle = { display: "block", fontFamily: font, fontSize: 12, fontWeight: 600, color: C.gray, margin: "12px 0 4px" };

function today(): string { return new Date().toISOString().slice(0, 10); }
function monthStart(): string { const now = new Date(); return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`; }
function monthEnd(): string { const now = new Date(); return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate()).padStart(2, "0")}`; }
function errorMessage(error: unknown, fallback: string): string { return error instanceof ApiError ? error.detail ?? error.message : fallback; }

function timeRangeLabel(startTime: string, endTime: string): string {
  const t = (value: string) => new Date(value).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  return `${t(startTime)}–${t(endTime)}`;
}

interface ConflictState {
  request: CreateCourtBlockRequest;
  bookings: CourtBookingDto[];
}

export function CourtBlocksTab() {
  const { accessToken, activeTenant } = useSession();
  const token = accessToken ?? "";
  const canManage = canManageCourtBookings(activeTenant?.roleSlug ?? null);
  const mutationRef = useRef(false);
  const [courts, setCourts] = useState<CourtDto[]>([]);
  const [blocks, setBlocks] = useState<CourtBlockDto[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [startDate, setStartDate] = useState(monthStart);
  const [endDate, setEndDate] = useState(monthEnd);
  const [courtId, setCourtId] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<CourtBlockDto | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [conflict, setConflict] = useState<ConflictState | null>(null);
  const [forcing, setForcing] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setBlocks(null);
    setLoadError(null);
    const startIso = new Date(`${startDate}T00:00:00`).toISOString();
    const endIso = new Date(`${endDate}T23:59:59.999`).toISOString();
    Promise.all([
      listCourts(token, { page: 1, pageSize: 100, isActive: true }),
      listCourtBlocks(token, { page: 1, pageSize: 100, startTime: startIso, endTime: endIso, courtId: courtId || undefined }),
    ])
      .then(([courtResult, blockResult]) => {
        if (cancelled) return;
        setCourts(courtResult.items);
        setBlocks(blockResult.items);
      })
      .catch(error => {
        if (cancelled) return;
        setBlocks([]);
        setLoadError(errorMessage(error, "Falha ao carregar os bloqueios."));
      });
    return () => { cancelled = true; };
  }, [token, startDate, endDate, courtId, reloadKey]);

  const addBlock = (created: CourtBlockDto) => setBlocks(current => [...(current ?? []), created].sort((a, b) => a.startTime.localeCompare(b.startTime)));

  const submitBlock = async (payload: CreateCourtBlockRequest) => {
    if (!token || mutationRef.current) return;
    mutationRef.current = true;
    setSubmitting(true);
    setActionError(null);
    try {
      addBlock(await createCourtBlock(token, payload));
      setShowForm(false);
      setSuccess("Bloqueio criado com sucesso.");
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        setShowForm(false);
        setConflict({ request: payload, bookings: (error.extensions?.conflictingBookings as CourtBookingDto[] | undefined) ?? [] });
      } else {
        setActionError(errorMessage(error, "Não foi possível criar o bloqueio."));
      }
    } finally {
      mutationRef.current = false;
      setSubmitting(false);
    }
  };

  const forceCancel = async () => {
    if (!token || !conflict || mutationRef.current) return;
    mutationRef.current = true;
    setForcing(true);
    setActionError(null);
    try {
      addBlock(await createCourtBlock(token, { ...conflict.request, forceCancelConflicts: true }));
      setConflict(null);
      setSuccess("Bloqueio criado com sucesso — reservas conflitantes canceladas.");
    } catch (error) {
      setActionError(errorMessage(error, "Não foi possível forçar o bloqueio."));
    } finally {
      mutationRef.current = false;
      setForcing(false);
    }
  };

  const confirmDelete = async () => {
    if (!token || !pendingDelete || mutationRef.current) return;
    mutationRef.current = true;
    setDeleting(true);
    setActionError(null);
    try {
      await deleteCourtBlock(token, pendingDelete.id);
      setBlocks(current => (current ?? []).filter(block => block.id !== pendingDelete.id));
      setPendingDelete(null);
      setSuccess("Bloqueio removido com sucesso.");
    } catch (error) {
      setActionError(errorMessage(error, "Não foi possível remover o bloqueio."));
    } finally {
      mutationRef.current = false;
      setDeleting(false);
    }
  };

  const names = new Map(courts.map(court => [court.id, court.name]));
  const courtLabel = (block: CourtBlockDto) => block.courtId ? (names.get(block.courtId) ?? "Quadra") : "Arena inteira";

  return <div style={{ display: "grid", gap: 14 }}>
    {success && <div role="status" style={{ color: C.ink, background: C.limeSoft, borderRadius: 10, padding: 12, fontSize: 12.5 }}>{success}</div>}
    {loadError && <div role="alert" style={{ color: C.red, background: C.white, border: `1px solid ${C.faint}`, borderRadius: 10, padding: 12, fontSize: 12.5 }}>{loadError}<Button variant="ghost" onClick={() => setReloadKey(value => value + 1)} style={{ marginLeft: 10 }}>Tentar novamente</Button></div>}
    {actionError && <div role="alert" style={{ color: C.red, fontSize: 12 }}>{actionError}</div>}
    <Card>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}><div><Label>Bloqueios de quadra/arena</Label><div style={{ color: C.light, fontSize: 12, marginTop: 3 }}>Reserve períodos de indisponibilidade (manutenção, torneios, eventos, clima) por quadra ou na arena inteira.</div></div>{canManage ? <Button onClick={() => { setActionError(null); setShowForm(true); }}>+ Novo bloqueio</Button> : <span style={{ color: C.light, background: C.bg, borderRadius: 12, padding: "5px 9px", fontSize: 11.5 }}>Somente leitura</span>}</div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 14 }}><label style={{ color: C.gray, fontSize: 12 }}>De <input aria-label="Data inicial" type="date" value={startDate} onChange={event => setStartDate(event.target.value)} style={{ ...fieldStyle, marginLeft: 6 }} /></label><label style={{ color: C.gray, fontSize: 12 }}>Até <input aria-label="Data final" type="date" value={endDate} onChange={event => setEndDate(event.target.value)} style={{ ...fieldStyle, marginLeft: 6 }} /></label><select aria-label="Filtrar bloqueios por quadra" value={courtId} onChange={event => setCourtId(event.target.value)} style={fieldStyle}><option value="">Todas as quadras</option>{courts.map(court => <option key={court.id} value={court.id}>{court.name}</option>)}</select></div>
      {blocks === null && !loadError && <div style={{ color: C.light, padding: "22px 0", fontSize: 13 }}>Carregando bloqueios...</div>}
      {blocks?.length === 0 && !loadError && <div style={{ color: C.light, padding: "22px 0", fontSize: 13 }}>Nenhum bloqueio no período.</div>}
      {blocks && blocks.length > 0 && <div style={{ display: "grid", gap: 9, marginTop: 14 }}>{blocks.map(block => <div key={block.id} style={{ border: `1px solid ${C.faint}`, borderRadius: 12, padding: 12, background: C.bg }}><div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}><div><b style={{ color: C.ink }}>{timeRangeLabel(block.startTime, block.endTime)} · {block.title}</b><div style={{ color: C.gray, fontSize: 12, marginTop: 4 }}>{courtLabel(block)} · {blockReasonTypeLabel(block.reasonType)}</div>{block.notes && <div style={{ color: C.light, fontSize: 11.5, marginTop: 4 }}>{block.notes}</div>}</div>{canManage && <Button variant="outline" ariaLabel={`Excluir bloqueio de ${block.title}`} onClick={() => { setActionError(null); setPendingDelete(block); }} style={{ color: C.red, borderColor: C.red, fontSize: 11.5, padding: "5px 11px" }}>Excluir</Button>}</div></div>)}</div>}
    </Card>
    {showForm && <CourtBlockForm courts={courts} date={today()} submitting={submitting} submitError={actionError} onSubmit={submitBlock} onCancel={() => !submitting && setShowForm(false)} />}
    {pendingDelete && (
      <ConfirmDialog
        title="Excluir bloqueio"
        confirmLabel="Confirmar exclusão"
        busy={deleting}
        busyLabel="Excluindo..."
        onConfirm={confirmDelete}
        onCancel={() => !deleting && setPendingDelete(null)}
      >
        Confirmar a exclusão do bloqueio <b style={{ color: C.ink }}>{pendingDelete.title}</b> ({timeRangeLabel(pendingDelete.startTime, pendingDelete.endTime)})?
      </ConfirmDialog>
    )}
    {conflict && (
      <CourtBlockConflictDialog
        request={conflict.request}
        bookings={conflict.bookings}
        busy={forcing}
        error={actionError}
        onForce={forceCancel}
        onCancel={() => !forcing && setConflict(null)}
      />
    )}
  </div>;
}

const REASON_OPTIONS = (Object.keys(BLOCK_REASON_TYPE) as Array<keyof typeof BLOCK_REASON_TYPE>).map(key => ({ value: String(BLOCK_REASON_TYPE[key]), label: blockReasonTypeLabel(BLOCK_REASON_TYPE[key]) }));

function CourtBlockForm({ courts, date, submitting, submitError, onSubmit, onCancel }: { courts: CourtDto[]; date: string; submitting: boolean; submitError: string | null; onSubmit: (payload: CreateCourtBlockRequest) => void; onCancel: () => void }) {
  const [title, setTitle] = useState("");
  const [reasonType, setReasonType] = useState("");
  const [courtId, setCourtId] = useState("");
  const [startTime, setStartTime] = useState(`${date}T08:00`);
  const [endTime, setEndTime] = useState(`${date}T10:00`);
  const [notes, setNotes] = useState("");
  const [errors, setErrors] = useState<string[]>([]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (submitting) return;
    const start = new Date(startTime);
    const end = new Date(endTime);
    const validationErrors = [
      !title.trim() && "Informe o título do bloqueio.",
      !reasonType && "Selecione o motivo do bloqueio.",
      startTime && endTime && end <= start && "O horário final deve ser posterior ao horário inicial.",
    ].filter(Boolean) as string[];
    setErrors(validationErrors);
    if (validationErrors.length) return;
    onSubmit({ courtId: courtId || null, title: title.trim(), reasonType: Number(reasonType), startTime: start.toISOString(), endTime: end.toISOString(), forceCancelConflicts: false, notes: notes.trim() || null });
  };

  return <div role="dialog" aria-modal="true" aria-label="Novo bloqueio" style={{ position: "fixed", inset: 0, background: C.overlay, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 16 }}>
    <Card style={{ width: "100%", maxWidth: 520, maxHeight: "90vh", overflowY: "auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}><Label style={{ color: C.ink }}>Novo bloqueio</Label><Button variant="ghost" ariaLabel="Fechar" onClick={onCancel}>x</Button></div>
      <form onSubmit={submit} noValidate>
        <label htmlFor="block-title" style={labelStyle}>Título *</label><FieldInput id="block-title" aria-label="Título" value={title} onChange={event => setTitle(event.target.value)} disabled={submitting} placeholder="Ex: Manutenção da rede" />
        <label htmlFor="block-reason" style={labelStyle}>Motivo *</label><select id="block-reason" aria-label="Motivo" value={reasonType} disabled={submitting} onChange={event => setReasonType(event.target.value)} style={{ ...fieldStyle, width: "100%", padding: 10 }}><option value="">Selecione</option>{REASON_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}</select>
        <label htmlFor="block-court" style={labelStyle}>Quadra</label><select id="block-court" aria-label="Quadra" value={courtId} disabled={submitting} onChange={event => setCourtId(event.target.value)} style={{ ...fieldStyle, width: "100%", padding: 10 }}><option value="">Arena inteira (todas as quadras)</option>{courts.map(court => <option key={court.id} value={court.id}>{court.name}</option>)}</select>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}><div><label htmlFor="block-start" style={labelStyle}>Início *</label><DateTimePicker id="block-start" ariaLabel="Início do bloqueio" value={startTime} onChange={setStartTime} disabled={submitting} placement="below" /></div><div><label htmlFor="block-end" style={labelStyle}>Fim *</label><DateTimePicker id="block-end" ariaLabel="Fim do bloqueio" value={endTime} onChange={setEndTime} disabled={submitting} placement="below" /></div></div>
        <label htmlFor="block-notes" style={labelStyle}>Observações</label><FieldTextarea id="block-notes" aria-label="Observações" value={notes} onChange={event => setNotes(event.target.value)} disabled={submitting} />
        {(errors.length > 0 || submitError) && <div role="alert" style={{ color: C.red, fontSize: 12, marginTop: 10 }}>{errors.map(error => <div key={error}>{error}</div>)}{submitError && <div>{submitError}</div>}</div>}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}><Button variant="outline" onClick={onCancel} disabled={submitting}>Cancelar</Button><Button type="submit" disabled={submitting}>{submitting ? "Criando..." : "Criar bloqueio"}</Button></div>
      </form>
    </Card>
  </div>;
}

function CourtBlockConflictDialog({ request, bookings, busy, error, onForce, onCancel }: { request: CreateCourtBlockRequest; bookings: CourtBookingDto[]; busy: boolean; error: string | null; onForce: () => void; onCancel: () => void }) {
  return <div role="dialog" aria-modal="true" aria-label="Reservas conflitantes" style={{ position: "fixed", inset: 0, background: C.overlay, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 16 }}>
    <Card style={{ width: "100%", maxWidth: 460 }}>
      <Label style={{ color: C.ink }}>Reservas conflitantes</Label>
      <p style={{ fontFamily: font, fontSize: 13.5, color: C.gray, lineHeight: 1.55, margin: "12px 0 14px" }}>O bloqueio <b style={{ color: C.ink }}>{request.title}</b> coincide com {bookings.length} reserva(s) existente(s). Confirme para cancelar as reservas e criar o bloqueio.</p>
      {bookings.length === 0 ? <div style={{ color: C.light, fontSize: 12.5, padding: "8px 0" }}>Nenhuma reserva detalhada disponível.</div> : <div style={{ display: "grid", gap: 6, marginBottom: 14 }}>{bookings.map(booking => <div key={booking.id} style={{ border: `1px solid ${C.faint}`, borderRadius: 10, padding: 10, background: C.bg }}><div style={{ fontWeight: 600, color: C.ink, fontSize: 13 }}>{timeRangeLabel(booking.startTime, booking.endTime)} · {booking.customerName}</div><div style={{ color: C.light, fontSize: 11.5, marginTop: 2 }}>{booking.customerPhone ?? "Sem telefone"}</div></div>)}</div>}
      {error && <div role="alert" style={{ color: C.red, fontSize: 12, marginBottom: 8 }}>{error}</div>}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <Button variant="outline" onClick={onCancel} disabled={busy}>Voltar</Button>
        <Button onClick={onForce} disabled={busy} style={{ background: C.red, color: C.white, border: "none" }}>{busy ? "Forçando..." : "Forçar bloqueio e cancelar reservas"}</Button>
      </div>
    </Card>
  </div>;
}